import AgreementSignModal from '@/components/config_components/AgreementSignModal/AgreementSignModal.vue';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import {
  hide_onboarding_system_loader_after_render,
  show_onboarding_system_loader
} from '@/views/onboarding_system_views/onboarding_ui_helpers';

function get_form_sections(form = {}) {
  return Array.isArray(form?.sections) ? form.sections : [];
}

function first_present_value(values = []) {
  return values.find((value) => String(value || '').trim()) || '';
}

function build_tab_id_for_section(form = {}, section = {}) {
  const form_id = first_present_value([form?.id, form?.form_name, 'section']);
  const section_id = first_present_value([section?.id, section?.label, 'item']);
  return `form-${form_id}-${section_id}`;
}

function tab_matches_profile(tab = {}) {
  const label = String(`${tab?.id || ''} ${tab?.label || ''}`).trim().toLowerCase();
  return ['profile', 'personal', 'applicant'].some((word) => label.includes(word));
}

function pick_reference_tab(tabs = []) {
  return tabs.find(tab_matches_profile) || tabs[0] || null;
}

const AGREEMENTS_STATE = {
    active_filter: 'all',
    selected_agreement: null,
    active_detail_tab: 'summary',
    is_detail_fullscreen: false,
    sign_modal: {
      visible: false,
      agreement_title: 'Agreement',
      signatory_name: '',
      document_id: 'DOC-AGREEMENT-2026'
    },
    filters: [
      { label: 'All', count: '4', tone: 'all' },
      { label: 'Open', tone: 'open' },
      { label: 'Mine', tone: 'mine' },
      { label: 'Blocked', tone: 'blocked' }
    ],
    detail_tabs: [
      { id: 'summary', label: 'Case Summary', icon: 'bi-grid' },
      { id: 'agreements', label: 'Agreements', icon: 'bi-folder', count: '1' }
    ],
    agreements: [
      {
        agreement: 'Supplier agreement',
        case_name: 'ABC Tech Solutions',
        counterparty: 'ABC Tech (Pty) Ltd',
        status: 'Drafted',
        status_tone: 'warning',
        blocks: 'Yes',
        action: 'Send for sig',
        context: 'supplier'
      },
      {
        agreement: 'Employee agreement',
        case_name: 'Sarah Khumalo',
        counterparty: 'Internal HR',
        status: 'Drafting',
        status_tone: 'warning',
        blocks: 'Yes',
        action: 'Continue',
        context: 'employee'
      },
      {
        agreement: 'Reseller agreement',
        case_name: 'NewGX Capital',
        counterparty: 'NewGX Capital',
        status: 'Awaiting CEO',
        status_tone: 'warning',
        blocks: 'Yes',
        action: 'Remind',
        context: 'partner'
      },
      {
        agreement: 'Internship MoU',
        case_name: 'Vusi Mthembu',
        counterparty: 'SETA',
        status: 'Drafting',
        status_tone: 'warning',
        blocks: 'Yes',
        action: 'Continue',
        context: 'intern'
      }
    ],
    detail_contexts: {
      supplier: {
        eyebrow: 'Supplier onboarding · Finance gate',
        title: 'ABC Tech Solutions',
        subtitle: 'Procurement Officer · Medium risk · 5 days in pipeline',
        badge: 'Blocked',
        badge_tone: 'danger',
        readiness: 62,
        progress_tone: 'warning',
        summary: [
          ['Journey', 'Supplier (high-risk template)'],
          ['Engagement', 'R 250 000 / year'],
          ['Owner', 'P. Naidoo · Procurement'],
          ['Submitted', '12 May 2026'],
          ['Activation means', 'Receive POs and payments']
        ],
        timeline: [
          { title: 'Submitted', detail: 'Initial documents captured · 12 May', status: 'done', icon: 'bi-check-lg' },
          { title: 'Profile', detail: 'Directors and addresses verified', status: 'done', icon: 'bi-check-lg' },
          { title: 'Documents', detail: 'Tax expired · BBBEE missing', status: 'current', icon: 'bi-exclamation-lg' },
          { title: 'Verification', detail: 'Awaiting valid tax certificate', status: 'locked', icon: 'bi-lock' },
          { title: 'Risk review', detail: 'Compliance review queued', status: 'locked', icon: 'bi-lock' }
        ],
        profile_title: 'Company details',
        profile: [
          ['Legal name', 'ABC Tech Solutions (Pty) Ltd'],
          ['Trading as', 'ABC Tech'],
          ['Registration no.', '2018/445120/07'],
          ['VAT no.', '4920288176'],
          ['Entity type', 'Private company'],
          ['Incorporated', '14 Mar 2018'],
          ['Employees', '45'],
          ['Industry', 'IT hardware & services'],
          ['Website', 'abctech.co.za']
        ],
        address: [
          ['Physical', 'Unit 12, Tech Park, 88 Marshall St, Johannesburg, 2001'],
          ['Postal', 'PO Box 5521, Johannesburg, 2000'],
          ['Country', 'South Africa']
        ],
        contacts: [
          { initials: 'DM', name: 'Dineo Mahlangu', detail: 'Primary contact · dineo@abctech.co.za · +27 11 555 0142' }
        ],
        questionnaire_count: '5 answers',
        questionnaire: [
          { question: 'Is the company a registered taxpayer in good standing?', answer: 'Yes — tax clearance attached', tone: 'danger', note: 'Certificate expired 03 May 2026', note_class: 'bg-danger-subtle text-danger-emphasis' },
          { question: 'Does any director hold a public office or PEP status?', answer: 'No', tone: 'success' },
          { question: 'Has the company been subject to litigation in the last 3 years?', answer: 'No', tone: 'success' },
          { question: 'Do you hold a valid BBBEE certificate?', answer: 'In renewal', tone: 'warning', note: 'Affidavit not yet uploaded', note_class: 'bg-warning-subtle text-warning-emphasis' },
          { question: 'Will any work be subcontracted?', answer: 'Yes — up to 20% (logistics)', tone: 'secondary' }
        ],
        signed_count: '1/3 signed',
        agreement_title: 'Supplier agreement',
        signatory: 'Dineo Mahlangu',
        agreement_note: 'Sent 12 May · awaiting counter-signature'
      },
      employee: {
        eyebrow: 'Employee onboarding · Training gate',
        title: 'Sarah Khumalo',
        subtitle: 'Junior Analyst · Permanent · 4 days in pipeline',
        badge: 'Access blocked',
        badge_tone: 'danger',
        readiness: 83,
        progress_tone: 'navy',
        summary: [
          ['Journey', 'Permanent employee'],
          ['Role', 'Junior Analyst · Finance'],
          ['Owner', 'T. Reddy · Head of Onboarding'],
          ['Manager', 'J. Moyo · CFO'],
          ['Activation means', 'System access + payroll']
        ],
        timeline: [
          { title: 'Offer accepted', detail: 'Signed contract · 8 May', status: 'done', icon: 'bi-check-lg' },
          { title: 'ID & address', detail: 'Home Affairs verified', status: 'done', icon: 'bi-check-lg' },
          { title: 'Policies', detail: 'POPIA + AUP acknowledged', status: 'done', icon: 'bi-check-lg' },
          { title: 'Training', detail: 'Cybersecurity 0/3', status: 'current', icon: 'bi-exclamation-lg' },
          { title: 'Assets', detail: 'Laptop & badge ready', status: 'locked', icon: 'bi-lock' }
        ],
        profile_title: 'Employee profile',
        profile: [
          ['Legal name', 'Sarah Khumalo'],
          ['Employee type', 'Permanent employee'],
          ['Department', 'Finance'],
          ['Manager', 'J. Moyo'],
          ['Country', 'South Africa']
        ],
        address: [],
        contacts: [
          { initials: 'TR', name: 'T. Reddy', detail: 'Onboarding owner · Head of Onboarding' },
          { initials: 'JM', name: 'J. Moyo', detail: 'Manager · CFO' }
        ],
        questionnaire_count: '4 answers',
        questionnaire: [
          { question: 'Has the employee accepted the offer?', answer: 'Yes - contract signed', tone: 'success' },
          { question: 'Have ID and address checks passed?', answer: 'Yes - Home Affairs verified', tone: 'success' },
          { question: 'Has cybersecurity training been completed?', answer: 'No - 0/3 modules complete', tone: 'warning', note: 'Training blocks IT access', note_class: 'bg-warning-subtle text-warning-emphasis' },
          { question: 'Is payroll ready for Day 1?', answer: 'Pending access provisioning', tone: 'secondary' }
        ],
        signed_count: '0/1 signed',
        agreement_title: 'Employee agreement',
        signatory: 'Sarah Khumalo',
        agreement_note: 'Awaiting signature'
      },
      partner: {
        eyebrow: 'Partner onboarding · DoA breach',
        title: 'NewGX Capital',
        subtitle: 'Enablement partner · R 520 000 reseller deal',
        badge: 'CEO approval',
        badge_tone: 'warning',
        readiness: 88,
        progress_tone: 'success',
        summary: [
          ['Journey', 'Enablement partner'],
          ['Deal value', 'R 520 000 / annual'],
          ['Owner', 'A. de Wet · Legal'],
          ['DoA rule', 'DoA-014 · value > R 500 000'],
          ['Activation means', 'Sales portal + commission']
        ],
        timeline: [
          { title: 'NDA', detail: 'Signed · 6 May', status: 'done', icon: 'bi-check-lg' },
          { title: 'Due diligence', detail: 'Clean · low risk', status: 'done', icon: 'bi-check-lg' },
          { title: 'Reseller agreement', detail: 'Legal cleared', status: 'done', icon: 'bi-check-lg' },
          { title: 'CFO approval', detail: 'Approved 11 May 23:50', status: 'done', icon: 'bi-check-lg' },
          { title: 'CEO approval', detail: 'Pending 18h', status: 'current', icon: 'bi-exclamation-lg' }
        ],
        profile_title: 'Company details',
        profile: [
          ['Legal name', 'NewGX Capital (Pty) Ltd'],
          ['Trading as', 'NewGX'],
          ['Registration no.', '2021/667301/07'],
          ['VAT no.', '4011559203'],
          ['Entity type', 'Private company'],
          ['Incorporated', '09 Feb 2021'],
          ['Employees', '22'],
          ['Industry', 'Financial services reseller'],
          ['Website', 'newgx.co.za']
        ],
        address: [
          ['Physical', '3rd Floor, The Forum, 2 Maude St, Sandton, 2196'],
          ['Postal', 'PO Box 78112, Sandton, 2146'],
          ['Country', 'South Africa']
        ],
        contacts: [
          { initials: 'Ad', name: 'Anke de Wet', detail: 'Primary contact · anke@newgx.co.za · +27 11 263 4400' }
        ],
        questionnaire_count: '4 answers',
        questionnaire: [
          { question: 'Annual contract value of the reseller engagement?', answer: 'R 520 000 / year', tone: 'warning', note: 'Exceeds R 500 000 - triggers DoA-014 (CEO)', note_class: 'bg-warning-subtle text-warning-emphasis' },
          { question: 'Are you authorised to resell in the listed territories?', answer: 'Yes - SADC region', tone: 'success' },
          { question: 'Any conflicts of interest with existing partners?', answer: 'No', tone: 'success' },
          { question: 'Confirm completion of product sales certification?', answer: 'Yes - 6/6 modules', tone: 'success' }
        ],
        signed_count: '1/2 signed',
        agreement_title: 'Reseller agreement',
        signatory: 'Anke de Wet',
        agreement_note: 'CFO signed · awaiting CEO under DoA-014'
      },
      intern: {
        eyebrow: 'Intern onboarding · Mentor assignment',
        title: 'Vusi Mthembu',
        subtitle: 'SETA intern · 12 month placement',
        badge: 'In progress',
        badge_tone: 'warning',
        readiness: 54,
        progress_tone: 'warning',
        summary: [
          ['Journey', 'SETA intern'],
          ['Host', 'Engineering · M. Khoza'],
          ['Owner', 'T. Reddy · Head of Onboarding'],
          ['Stipend', 'R 6 500 / month'],
          ['Activation means', 'Workplace agreement + stipend']
        ],
        timeline: [
          { title: 'ID & address', detail: 'Verified', status: 'done', icon: 'bi-check-lg' },
          { title: 'Host confirmed', detail: 'Engineering · M. Khoza', status: 'done', icon: 'bi-check-lg' },
          { title: 'Mentor assignment', detail: 'Awaiting allocation', status: 'current', icon: 'bi-exclamation-lg' },
          { title: 'MoU drafting', detail: 'Internship MoU template', status: 'locked', icon: 'bi-lock' }
        ],
        profile_title: 'Intern profile',
        profile: [
          ['Legal name', 'Vusi Mthembu'],
          ['Programme', 'SETA intern'],
          ['Placement', 'Engineering'],
          ['Host', 'M. Khoza'],
          ['Country', 'South Africa']
        ],
        address: [],
        contacts: [
          { initials: 'TR', name: 'T. Reddy', detail: 'Onboarding owner · Head of Onboarding' }
        ],
        questionnaire_count: '3 answers',
        questionnaire: [
          { question: 'Is the host department confirmed?', answer: 'Yes - Engineering', tone: 'success' },
          { question: 'Has a mentor been allocated?', answer: 'Pending', tone: 'warning', note: 'Mentor assignment required before activation', note_class: 'bg-warning-subtle text-warning-emphasis' },
          { question: 'Is stipend setup complete?', answer: 'In progress', tone: 'secondary' }
        ],
        signed_count: '0/1 signed',
        agreement_title: 'Intern agreement',
        signatory: 'Vusi Mthembu',
        agreement_note: 'Awaiting signature'
      }
    }
  
};

export default {
  name: 'OnboardingManageAgreements',
  components: {
    AgreementSignModal
  },
  data: () => ({
    ...AGREEMENTS_STATE,
    sign_modal: { ...AGREEMENTS_STATE.sign_modal },
    ...tablePaginationState()
  }),
  computed: {
    table_total_rows() {
      return this.filtered_agreements.length;
    },
    filtered_agreements() {
      return this.filter_table_rows(this.agreements);
    },
    paginated_agreements() {
      return this.paginate_table_rows(this.filtered_agreements);
    },
    ...tablePaginationComputed,
    selected_context() {
      if (this.selected_agreement?.context === 'supplier') {
        return this.detail_contexts.supplier;
      }

      if (this.selected_agreement?.context === 'partner') {
        return this.detail_contexts.partner;
      }

      if (this.selected_agreement?.context === 'intern') {
        return this.detail_contexts.intern;
      }

      return this.detail_contexts.employee;
    },
    selected_context_form_sections() {
      return [
        this.build_context_form_section(this.selected_context.profile_title, this.selected_context.profile),
        this.build_context_form_section('Address', this.selected_context.address),
        this.build_questionnaire_form_section(this.selected_context.questionnaire)
      ].filter((form_section) => form_section.sections[0].sub_sections[0].questions.length);
    },
    active_form_section() {
      const forms = this.selected_context_form_sections;
      for (const form of forms) {
        const matched_section = get_form_sections(form)
          .find((section) => build_tab_id_for_section(form, section) === this.active_detail_tab);
        if (matched_section) return matched_section;
      }
      return null;
    },
    computed_detail_tabs() {
      const section_tabs = [];
      this.selected_context_form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          section_tabs.push({
            id: this.tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: 'bi-list-check'
          });
        });
      });
      const reference_tab = pick_reference_tab(section_tabs);
      const tabs = [
        {
          id: 'agreements',
          label: 'Required Action: Agreement Review',
          icon: 'bi-folder',
          count: this.selected_context.signed_count,
          badge: 'Current'
        }
      ];
      if (reference_tab) {
        tabs.push({
          ...reference_tab,
          label: 'Application Reference',
          badge: 'Reference'
        });
      }
      tabs.push({ id: 'summary', label: 'Case Summary', icon: 'bi-grid' });
      section_tabs
        .filter((tab) => tab.id !== reference_tab?.id)
        .forEach((tab) => {
          tabs.push(tab);
        });
      return tabs;
    }
  },
  methods: {
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    tab_id_for_section(form, section) {
      return build_tab_id_for_section(form, section);
    },
    open_agreement_detail(agreement_item) {
      show_onboarding_system_loader('Loading contracts drawer...');
      this.selected_agreement = agreement_item;
      this.active_detail_tab = 'agreements';
      this.is_detail_fullscreen = false;
      hide_onboarding_system_loader_after_render(this);
    },
    close_agreement_detail() {
      this.selected_agreement = null;
      this.is_detail_fullscreen = false;
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    current_detail_action_tab() {
      return this.computed_detail_tabs.find((tab) => String(tab?.badge || '').trim().toLowerCase() === 'current') || this.computed_detail_tabs[0] || null;
    },
    current_detail_action_copy() {
      return 'Prepare the agreement pack, confirm the right signatories, and send or follow up on signatures before the case progresses.';
    },
    open_selected_agreement_profile() {
      const first_section_tab = this.computed_detail_tabs.find((tab) => !['summary', 'agreements'].includes(tab.id));
      this.active_detail_tab = first_section_tab?.id || 'summary';
    },
    build_context_form_section(label = 'Application details', rows = []) {
      const slug = String(label || 'application-details').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return {
        id: slug,
        form_name: 'Application Details',
        sections: [{
          id: slug,
          label,
          sub_sections: [{
            id: `${slug}-answers`,
            label,
            questions: rows.map((row) => ({
              id: String(row[0] || '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
              label: row[0],
              answer: row[1]
            }))
          }]
        }]
      };
    },
    build_questionnaire_form_section(rows = []) {
      const section = this.build_context_form_section('Compliance questionnaire', []);
      section.sections[0].sub_sections[0].questions = rows.map((row) => ({
        id: String(row.question || '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        label: row.question,
        answer: row.answer
      }));
      return section;
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    open_sign_modal(agreement_title = 'Agreement', signatory_name = '') {
      this.sign_modal = {
        visible: true,
        agreement_title,
        signatory_name: signatory_name || this.selected_context.signatory,
        document_id: 'DOC-AGREEMENT-2026'
      };
    },
    close_sign_modal() {
      this.sign_modal.visible = false;
    },
    complete_signature() {
      this.close_sign_modal();
    },
    status_badge_class(tone) {
      if (tone === 'danger') {
        return 'bg-danger-subtle text-danger-emphasis';
      }

      if (tone === 'success') {
        return 'bg-success-subtle text-success-emphasis';
      }

      if (tone === 'warning') {
        return 'bg-warning-subtle text-warning-emphasis';
      }

      if (tone === 'secondary') {
        return 'bg-secondary-subtle text-secondary-emphasis';
      }

      return '';
    },
    progress_bar_color(tone) {
      if (tone === 'warning') {
        return '#bf4b08';
      }

      if (tone === 'success') {
        return '#417d71';
      }

      return '#416f6e';
    },
    answer_dot_class(tone) {
      if (tone === 'danger') {
        return 'bg-danger';
      }

      if (tone === 'success') {
        return 'bg-success';
      }

      if (tone === 'warning') {
        return 'bg-warning';
      }

      return 'bg-secondary';
    }
  }
};
